// istanbul ignore file
import React from 'react'
import { Box, Typography, CircularProgress, Table, TableBody, TableContainer, TableHead, TableRow, Divider, Stack } from '@mui/material'
import InfoVector from '../../../assets/InfoVector.png'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import LanguageStrings from '../../../i18n/locales'
import TableSortLabel from '@mui/material/TableSortLabel'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import moment from 'moment-timezone'
import { styled } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'

export type Order = 'asc' | 'desc'
export interface Sort {
  order: Order
  orderBy: string
}
interface Alarm {
  severity: string | null
  activeSince: string | null
  parameterName: string
  deviceName?: string
  status: string
}
interface ParentProps {
  sort: Sort
  setSort: React.Dispatch<React.SetStateAction<Sort>>
  title: string
  color: string
  infoText?: string
  score: number
  isLoading: boolean
  alarms: Alarm[] | []
}
const AssetDashboardStrings = LanguageStrings().AssetDashboardStrings

interface Data {
  parameterName: string
  deviceName: string
  status: string
  severity: string
  activeSince: string
}
interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}
const headCells: readonly HeadCell[] = [
  {
    id: 'parameterName', numeric: false, disablePadding: false, label: AssetDashboardStrings.ParameterName, sortable: false
  },
  {
    id: 'deviceName', numeric: false, disablePadding: false, label: 'DEVICE NAME', sortable: false
  },
  {
    id: 'status', numeric: false, disablePadding: false, label: AssetDashboardStrings.STATUS, sortable: false
  },
  {
    id: 'severity', numeric: false, disablePadding: false, label: AssetDashboardStrings.SEVERITY, sortable: false
  },
  {
    id: 'activeSince', numeric: false, disablePadding: false, label: AssetDashboardStrings.ACTIVE_SINCE, sortable: true
  }
]

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    padding: '18px 12px',
    fontFamily: 'Honeywell Sans Web',
    borderBottom: '1px solid #101010'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '16px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web'
  }
}))

function EnhancedTableHead (props: any): any {
  const { order, orderBy, onRequestSort } = props
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy)
    }
  return (
    <TableHead sx={{ backgroundColor: '#202020' }} data-testid="table-header">
      <TableRow sx={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: '#202020' }}>
        {headCells.map((headCell: HeadCell) => (
          <StyledTableCell key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              sx={{
                '& .MuiTableSortLabel-icon': {
                  opacity: 1
                }
              }}
              hideSortIcon={!headCell.sortable}
              direction={orderBy === headCell.id ? order : 'desc'}
              onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
              aria-label={headCell.label} data-testid="test-sort"
            >
              {headCell.label}
              {orderBy === headCell.id
                ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
                  )
                : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

const AssetAlert = (props: ParentProps): JSX.Element => {
  const handleRequestSort = (event: React.MouseEvent<unknown>, newOrderBy: keyof Data): void => {
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === 'asc'
    const toggledOrder = isAsc ? 'desc' : 'asc'
    props.setSort({ order: toggledOrder, orderBy: newOrderBy })
  }
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return (
    <>
      <Box sx={{ mb: 4, minHeight: '250px', backgroundColor: '#333', p: 3, border: '1px solid #000', borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: '600', color: '#FFF', fontFamily: 'Honeywell Sans Web' }}>
          {props?.title}
          <Tooltip title={props?.infoText} placement="right" arrow>
            <IconButton size="small">
              <img style={{ width: '15px', height: '15px', marginLeft: '5px' }} src={InfoVector} />
            </IconButton>
          </Tooltip>
        </Typography>
        {props.isLoading
          ? (
            <div style={{ height: '120px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>
            )
          : (
            <Stack direction="row" alignItems="flex-start" justifyContent="flex-start" spacing={2}>
              {(props.title === 'SAFETY' || props.title === 'RELIABILITY' || props.title === 'EFFICIENCY')
                ? (
                  <Box position="relative" display="inline-flex" style={{ width: '290px', marginTop: '20px', marginLeft: '32px' }}>
                    <CircularProgress variant="determinate" value={100} size={235} thickness={3} sx={{ color: '#202020' }} />
                    <CircularProgress variant="determinate" value={props?.score ?? 0} size={235} thickness={3} sx={{ color: props?.color, position: 'absolute', left: 0 }} />
                    <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: '600', fontFamily: 'Honeywell Sans Web' }} component="div" color="white">
                        {`${props?.score.toFixed(2)}`}
                        <span style={{ fontSize: '16px', color: '#5E5E5E' }}> %</span>
                      </Typography>
                    </Box>
                  </Box>
                  )
                : (
                  <Box position="relative" display="inline-flex" style={{ backgroundColor: '#333', width: '290px', marginTop: '20px', marginLeft: '32px' }}></Box>
                  )
              }
              <Divider className="asset-divider" orientation="vertical" flexItem sx={{ mx: 10, backgroundColor: '#FFF' }} />
              <div className="assett" style={{ width: '100%' }}>
                <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: '600', fontFamily: 'Honeywell Sans Web', color: '#FFF', marginTop: '-20px', position: 'absolute' }}>
                  {`${props?.title} SOURCES (${props?.alarms?.length ?? 0})`}
                </Typography>
                <div style={{ height: '15px', backgroundColor: '#333333' }}></div>
                <TableContainer className="asset-score-table" sx={{ border: '1px solid #101010', mt: 2, overflow: 'auto !important', borderRadius: '5px', height: '230px' }}>
                  <Table style={{ borderRadius: '8px', marginTop: '0px', backgroundColor: '#333333' }} aria-label={`${props?.title} sources`}>
                    <EnhancedTableHead
                      order={props.sort.order} orderBy={props.sort.orderBy}
                      onRequestSort={handleRequestSort}
                    />
                    <TableBody sx={{ backgroundColor: '#303030' }}>
                      { props?.alarms?.length > 0
                        ? props?.alarms?.map((source, idx) => (
                          <TableRow key={idx} sx={{ borderBottom: '1px solid #101010' }}>
                            <TableCell sx={{ width: '25%', padding: '17px 12px', fontSize: '16px', fontFamily: 'Honeywell Sans Web', color: '#F0F0F0', borderBottom: '1px solid #101010' }}>
                              {source.parameterName}
                            </TableCell>
                            <TableCell sx={{ width: '25%', padding: '17px 12px', color: '#F0F0F0', fontSize: '16px', fontFamily: 'Honeywell Sans Web', borderBottom: '1px solid #101010' }}>
                              {source.deviceName}
                            </TableCell>
                            <TableCell sx={{ width: '15%', padding: '17px 12px', color: '#F0F0F0', fontSize: '16px', fontFamily: 'Honeywell Sans Web', borderBottom: '1px solid #101010' }}>
                              {source.status}
                            </TableCell>
                            <TableCell sx={{ width: '15%', padding: '17px 12px', color: '#F0F0F0', fontSize: '16px', fontFamily: 'Honeywell Sans Web', borderBottom: '1px solid #101010' }}>
                              {source.severity ?? '-'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '16px', padding: '17px 12px', fontFamily: 'Honeywell Sans Web', color: '#F0F0F0', borderBottom: '1px solid #101010' }}>
                              {source.activeSince !== null && source.activeSince !== '' ? moment.utc(source.activeSince).tz(userTimezone).format('hh:mm A, MMM DD YYYY') : '-'}
                            </TableCell>
                          </TableRow>
                        ))
                        : (<TableRow sx={{ borderBottom: '1px solid #101010' }}>
                            <TableCell colSpan={5} sx={{ borderBottom: '1px solid #101010' }}>
                              <Typography sx={{ fontSize: '16px', fontFamily: 'Honeywell Sans Web', color: '#F0F0F0' }}>
                                No Records found
                              </Typography>
                            </TableCell>
                          </TableRow>
                          )
                      }
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Stack>
            )
        }
      </Box>
    </>
  )
}

export default AssetAlert
